// client/src/pages/admin/StoryEditor.js

import { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import storyService from '../../services/storyService.js';

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline'],
    ['blockquote'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['link', 'image'],
    [{ align: [] }],
    ['clean']
  ]
};

const quillFormats = [
  'header', 'bold', 'italic', 'underline',
  'blockquote', 'list', 'bullet',
  'link', 'image', 'align'
];

export default function StoryEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = !id;

  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showPreview, setShowPreview] = useState(false);

  const [form, setForm] = useState({
    categoryid: '',
    subcategoryid: '',
    title: '',
    slug: '',
    author: '',
    content: '',
    contenthtml: '',
    contentformat: 'html',
    excerpt: '',
    coverimageurl: '',
    pagecount: 1,
    readtime: 5,
    displayorder: 0,
    isfeatured: false,
    ispublished: false
  });

  useEffect(() => {
    loadOptions();
    if (!isNew) loadStory();
  }, [id]);

  useEffect(() => {
    if (form.categoryid) {
      storyService.getSubcategories(form.categoryid)
        .then((res) => setSubcategories(res.subcategories || []))
        .catch(() => {});
    } else {
      setSubcategories([]);
    }
  }, [form.categoryid]);

  const loadOptions = async () => {
    try {
      const catsRes = await storyService.getCategories();
      setCategories(catsRes.categories || []);
    } catch (err) {
      setError('Failed to load categories');
    }
  };

  const loadStory = async () => {
    try {
      setLoading(true);
      const res = await storyService.adminGetStory(id);
      const s = res.story;
      setForm({
        categoryid: s.categoryid || '',
        subcategoryid: s.subcategoryid || '',
        title: s.title || '',
        slug: s.slug || '',
        author: s.author || '',
        content: s.content || '',
        contenthtml: s.contenthtml || '',
        contentformat: s.contentformat || 'html',
        excerpt: s.excerpt || '',
        coverimageurl: s.coverimageurl || '',
        pagecount: s.pagecount || 1,
        readtime: s.readtime || 5,
        displayorder: s.displayorder || 0,
        isfeatured: s.isfeatured || false,
        ispublished: s.ispublished || false
      });
    } catch (err) {
      setError('Failed to load story');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    setError('');
    setSuccess('');
  };

  const generateSlug = () => {
    const slug = form.title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
    handleChange('slug', slug);
  };

  const handleSave = async () => {
    if (!form.title || !form.categoryid) {
      setError('Title and category are required');
      return;
    }
    try {
      setSaving(true);
      setError('');
      if (isNew) {
        const res = await storyService.adminCreateStory(form);
        setSuccess('Story created!');
        navigate(`/admin/stories/edit/${res.storyid}`, { replace: true });
      } else {
        await storyService.adminUpdateStory(id, form);
        setSuccess('Story saved!');
      }
    } catch (err) {
      setError('Failed to save story');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="w-8 h-8 border-2 border-royal-600 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-8">
      {/* Dashboard Back Link */}
      <div className="mb-5">
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg bg-gold text-royal-900 font-semibold text-base hover:bg-gold-light transition-colors"
        >
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          Dashboard
        </Link>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <button
            onClick={() => navigate('/admin/stories')}
            className="text-gold hover:text-gold-light text-sm transition-colors mb-2 block"
          >
            &larr; Back to Stories
          </button>
          <h1 className="font-display text-2xl text-gold">
            {isNew ? 'New Story' : 'Edit Story'}
          </h1>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowPreview(!showPreview)}
            className="px-4 py-2 border border-royal-600 text-royal-200 rounded-lg text-sm hover:border-gold/50 hover:text-gold transition-colors"
          >
            {showPreview ? 'Edit' : 'Preview'}
          </button>
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2 bg-gold text-royal-900 font-semibold rounded-lg text-sm hover:bg-gold-light transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-300 text-base mb-4">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-royal-800 border border-gold/30 rounded-lg text-gold text-base mb-4">{success}</div>
      )}

      {showPreview ? (
        <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6">
          <h2 className="font-display text-xl text-gold mb-1">{form.title || 'Untitled'}</h2>
          {form.author && <p className="text-royal-200 italic text-sm mb-4">by {form.author}</p>}
          <div
            className="prose prose-invert max-w-none text-royal-100 leading-relaxed story-content"
            dangerouslySetInnerHTML={{ __html: form.contentformat === 'html' ? form.contenthtml : form.content }}
          />
        </div>
      ) : (
        <div className="space-y-6">

          {/* Basic Info */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6">
            <h2 className="text-white text-base font-semibold mb-4">Basic Info</h2>
            <div className="space-y-4">
              <div>
                <label className="text-royal-200 text-sm block mb-1">Title *</label>
                <input
                  value={form.title}
                  onChange={(e) => handleChange('title', e.target.value)}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors"
                  placeholder="Story title"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-royal-200 text-sm block mb-1">Slug</label>
                  <div className="flex gap-2">
                    <input
                      value={form.slug}
                      onChange={(e) => handleChange('slug', e.target.value)}
                      className="flex-1 px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors"
                      placeholder="story-url-slug"
                    />
                    <button
                      onClick={generateSlug}
                      className="px-3 py-2.5 border border-royal-600 text-royal-200 rounded-lg text-sm hover:text-gold hover:border-gold/50 transition-colors"
                    >
                      Auto
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-royal-200 text-sm block mb-1">Author</label>
                  <input
                    value={form.author}
                    onChange={(e) => handleChange('author', e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors"
                    placeholder="Author name"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-royal-200 text-sm block mb-1">Category *</label>
                  <select
                    value={form.categoryid}
                    onChange={(e) => {
                      handleChange('categoryid', e.target.value);
                      handleChange('subcategoryid', '');
                    }}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold transition-colors"
                  >
                    <option value="">Select category</option>
                    {categories.map((c) => (
                      <option key={c.categoryid} value={c.categoryid}>{c.categoryname}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="text-royal-200 text-sm block mb-1">Subcategory</label>
                  <select
                    value={form.subcategoryid}
                    onChange={(e) => handleChange('subcategoryid', e.target.value)}
                    className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white focus:outline-none focus:border-gold transition-colors"
                  >
                    <option value="">None</option>
                    {subcategories.map((s) => (
                      <option key={s.subcategoryid} value={s.subcategoryid}>{s.subcategoryname}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-royal-200 text-sm block mb-1">Excerpt</label>
                <textarea
                  value={form.excerpt}
                  onChange={(e) => handleChange('excerpt', e.target.value)}
                  rows={2}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors resize-y"
                  placeholder="Short description for story cards"
                />
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-white text-base font-semibold">Content</h2>
              <div className="flex items-center gap-2">
                <span className="text-royal-200 text-sm">Format:</span>
                <select
                  value={form.contentformat}
                  onChange={(e) => handleChange('contentformat', e.target.value)}
                  className="px-3 py-1.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold transition-colors"
                >
                  <option value="html">Rich Editor (HTML)</option>
                  <option value="text">Plain Text</option>
                </select>
              </div>
            </div>

            {form.contentformat === 'html' ? (
              <div className="quill-dark">
                <ReactQuill
                  theme="snow"
                  value={form.contenthtml}
                  onChange={(val) => handleChange('contenthtml', val)}
                  modules={quillModules}
                  formats={quillFormats}
                  placeholder="Write your story here... Use the image button to insert Supabase image URLs."
                  style={{ minHeight: '400px' }}
                />
              </div>
            ) : (
              <textarea
                value={form.content}
                onChange={(e) => handleChange('content', e.target.value)}
                rows={20}
                className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-base text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors resize-y font-mono"
                placeholder="Plain text content. Use ---PAGE_BREAK--- to separate pages."
              />
            )}
          </div>

          {/* Settings */}
          <div className="bg-royal-800/40 border border-royal-600 rounded-xl p-6">
            <h2 className="text-white text-base font-semibold mb-4">Settings</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <label className="text-royal-200 text-sm block mb-1">Cover Image URL</label>
                <input
                  value={form.coverimageurl}
                  onChange={(e) => handleChange('coverimageurl', e.target.value)}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white placeholder-royal-200 focus:outline-none focus:border-gold transition-colors"
                  placeholder="https://..."
                />
              </div>
              <div>
                <label className="text-royal-200 text-sm block mb-1">Page Count</label>
                <input
                  type="number"
                  value={form.pagecount}
                  onChange={(e) => handleChange('pagecount', parseInt(e.target.value) || 1)}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold transition-colors"
                />
              </div>
              <div>
                <label className="text-royal-200 text-sm block mb-1">Read Time (min)</label>
                <input
                  type="number"
                  value={form.readtime}
                  onChange={(e) => handleChange('readtime', parseInt(e.target.value) || 5)}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold transition-colors"
                />
              </div>
              <div>
                <label className="text-royal-200 text-sm block mb-1">Display Order</label>
                <input
                  type="number"
                  value={form.displayorder}
                  onChange={(e) => handleChange('displayorder', parseInt(e.target.value) || 0)}
                  className="w-full px-3 py-2.5 bg-royal-800/50 border border-royal-600 rounded-lg text-sm text-white focus:outline-none focus:border-gold transition-colors"
                />
              </div>
            </div>
            <div className="flex items-center gap-6 mt-4">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.ispublished}
                  onChange={(e) => handleChange('ispublished', e.target.checked)}
                  className="w-5 h-5 rounded border border-royal-600 accent-gold"
                />
                <span className="text-white text-base">Published</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.isfeatured}
                  onChange={(e) => handleChange('isfeatured', e.target.checked)}
                  className="w-5 h-5 rounded border border-royal-600 accent-gold"
                />
                <span className="text-white text-base">Featured</span>
              </label>
            </div>
          </div>

          {/* Save */}
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={() => navigate('/admin/stories')}
              className="px-5 py-2.5 border border-royal-600 text-royal-200 rounded-lg text-base hover:border-gold/50 hover:text-gold transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-5 py-2.5 bg-gold text-royal-900 font-semibold rounded-lg text-base hover:bg-gold-light transition-colors disabled:opacity-50"
            >
              {saving ? 'Saving...' : isNew ? 'Create Story' : 'Save Changes'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}